import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableCellHead,
  TableHead,
  TableRow,
} from '~/ui/Table';
import styles from './EventsTable.module.scss';
import EventType from './EventType/EventType';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { IUserRoles } from '~/pages/Events/Events';
import { getPaginate } from '~/utils/getPaginate';
import { paginateEnums } from '~/utils/types/paginateEnums';
import ChevronIcon from '~/assets/svg/newSvg/chevron-right.svg?react';
import cn from 'classnames';

interface IEventProps {
  id: number;
  created_at: string;
  user_id: number;
  type: { display_name: string; id: number };
  user: {
    code: number;
    email?: string;
    user_role: { id: number; name: string; text: string }[];
    role_id: number;
  };
  event: string;
  event_name: string;
  description_text?: string;
  description?: {
    human_description?: string;
    object_name?: string | null;
    changed_fields?: string[] | null;
    method?: string;
    url?: string;
  } | null;
}

interface IEventsTableProps {
  events: IEventProps[];
  sort: any;
  setSort: ({ field, direction }: any) => void;
  userRoles: IUserRoles[] | undefined;
}

const ExpandedDescription = ({
  event,
  t,
}: {
  event: IEventProps;
  t: (key: string) => string;
}) => {
  const desc = event.description;

  if (desc?.human_description) {
    return (
      <div className={styles.descriptionList}>
        <div className={styles.descriptionItem}>
          <span className={styles.descriptionLabel}>{t('action')}</span>
          <span className={styles.descriptionValue}>{desc.human_description}</span>
        </div>
        {desc.object_name && (
          <div className={styles.descriptionItem}>
            <span className={styles.descriptionLabel}>{t('object')}</span>
            <span className={styles.descriptionValue}>{desc.object_name}</span>
          </div>
        )}
        {!!desc.changed_fields?.length && (
          <div className={styles.descriptionItem}>
            <span className={styles.descriptionLabel}>{t('changed_fields_label')}</span>
            <div className={styles.changedFields}>
              {desc.changed_fields.map(field => (
                <span key={field} className={styles.fieldTag}>{field}</span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  if (desc?.method && desc?.url) {
    return (
      <div className={styles.descriptionList}>
        <div className={styles.descriptionItem}>
          <span className={styles.descriptionLabel}>{t('action')}</span>
          <span className={styles.descriptionValue}>{event.event_name || event.event}</span>
        </div>
        <div className={styles.descriptionItem}>
          <span className={styles.descriptionLabel}>{t('request_label')}</span>
          <span className={styles.descriptionValue}>
            <span className={styles.methodBadge}>{desc.method}</span> {desc.url}
          </span>
        </div>
      </div>
    );
  }

  return <span>{event.event_name || event.event || '-'}</span>;
};

const EventsTable = ({
  events,
  sort,
  setSort,
  userRoles,
}: IEventsTableProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { value, paginate } = getPaginate(paginateEnums.EVENT_PAGINATE);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const handleRowClick = (event: IEventProps) => {
    setExpandedId(prev => (prev === event.id ? null : event.id));
  };

  const handleNavigate = (event: IEventProps) => {
    if (event.user_id) {
      navigate(
        `/user/${event.user_id}/events?date=${dayjs(event.created_at).format(
          'DD.MM.YYYY'
        )}&${value}=${paginate}`
      );
    }
  };

  return (
    <Table>
      <TableHead sort={sort} setSort={setSort}>
        <TableCellHead field="type_id">{t('event')}</TableCellHead>
        <TableCellHead field="user_id">{t('initiator')}</TableCellHead>
        <TableCellHead field="role_id" sortable={false}>
          {t('user_role')}
        </TableCellHead>
        <TableCellHead field="created_at">{t('event_date')}</TableCellHead>
        <TableCellHead field="event">{t('event_type')}</TableCellHead>
      </TableHead>
      <TableBody>
        {events?.map(event => {
          const role = userRoles?.find(
            item => item.id === event?.user?.role_id
          );
          const isExpanded = expandedId === event.id;
          return (
            <>
              <TableRow
                key={event?.id}
                onClick={() => handleRowClick(event)}
                style={cn(styles.row, isExpanded && styles.rowActive)}
              >
                <TableCell>
                  <span className={styles.chevronWrapper}>
                    <ChevronIcon
                      className={cn(
                        styles.chevron,
                        isExpanded && styles.chevronExpanded
                      )}
                    />
                    {event?.type?.display_name}
                  </span>
                </TableCell>
                <TableCell className={styles.nameCell}>
                  <span
                    className={styles.navigateLink}
                    onClick={e => {
                      e.stopPropagation();
                      handleNavigate(event);
                    }}
                  >
                    {event?.user?.email || '-'}
                  </span>
                </TableCell>
                <TableCell className={styles.nameCell}>
                  {role?.display_name || '-'}
                </TableCell>
                <TableCell>
                  {dayjs(event?.created_at).format('DD.MM.YYYY HH:mm:ss')}
                </TableCell>
                <TableCell className={styles.typeCell}>
                  <EventType
                    eventType={event?.event}
                    eventName={event?.event_name}
                  />
                </TableCell>
              </TableRow>
              {isExpanded && (
                <tr key={`${event.id}-desc`} className={styles.expandedRow}>
                  <td colSpan={5} className={styles.expandedCell}>
                    <ExpandedDescription event={event} t={t} />
                  </td>
                </tr>
              )}
            </>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default EventsTable;
